import { useEffect, useRef, useState } from 'react';
import { useFeature } from '../../lib/features';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../store/StoreContext';
import {
  fetchChannelActivity,
  fetchChannelMember,
  fetchMyChannelStats,
  followChannel,
  likeChannel,
  muteChannel,
  unfollowChannel,
  unlikeChannel,
  unmuteChannel,
  type ChannelActivity,
  type ChannelMember,
} from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AvatarView } from '../../components/ui/AvatarView';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { FlowHashtagText } from '../../components/ui/FlowHashtagText';
import { WrapTags } from '../../components/ui/WrapTags';
import { EmptyState } from '../../components/ui/EmptyState';
import { Sheet } from '../../components/ui/Sheet';
import { Toast } from '../../components/ui/Toast';
import { IconButton } from '../../components/ui/IconButton';
import { Bell, BellOff, Copy, MessageCircle, SquarePen } from 'lucide-react';

function Stat({ title, value }: { title: string; value: number }) {
  return (
    <div className="flex-1 flex flex-col items-center gap-0.5">
      <span className="font-semibold">{value}</span>
      <span className="text-xs text-black/50 dark:text-white/50">{title}</span>
    </div>
  );
}

export function ChannelDetailsScreen() {
  const cloneOn = useFeature('channels.clone');
  const { channelId } = useParams();
  const navigate = useNavigate();
  const {
    channel,
    ensureChannelLoaded,
    applyChannelStats,
    addChannelToPersonalList,
    removeChannelFromPersonalList,
    syncChannels,
    isChannelOwner,
    isLoggedIn,
    setSearchQuery,
    setSelectedTab,
    cloneChannel,
    leaveChannel,
    profile,
  } = useStore();
  const [showViewer, setShowViewer] = useState(false);
  const [busy, setBusy] = useState<'like' | 'follow' | 'mute' | 'clone' | 'leave' | null>(null);
  // See the fetchMyChannelStats effect's own comment below for what this
  // guards against.
  const myStatsGenRef = useRef(0);
  const [error, setError] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // The caller's own member doc for this channel - undefined while
  // loading, null if they have none. Distinguishes a real member (invited
  // & accepted / approved request) from a plain follower, which decides
  // whether the button below reads "Leave channel" or "Unfollow".
  const [myMember, setMyMember] = useState<ChannelMember | null | undefined>(undefined);
  // Activity stats (Phase 2): message count, member count, live "watching
  // now". Null until the first load; refreshed whenever the screen mounts.
  const [activity, setActivity] = useState<ChannelActivity | null>(null);

  const c = channel(channelId ?? '');

  // `channels` (what `channel()` reads first) is scoped to the caller's
  // own channels now - a public channel reached via a Search result, not
  // yet followed/joined, isn't in it. See ensureChannelLoaded's own
  // comment for the fallback fetch this triggers.
  useEffect(() => {
    ensureChannelLoaded(channelId ?? '');
  }, [channelId, ensureChannelLoaded]);

  // The public likes/followers counts already come back with the channel
  // itself (see dataApi.ts's fetchChannel merging in GET /stats/channel/
  // {nickname}) - "did *I* like/follow this" is a separate, authenticated
  // call (GET /stats/channel/{nickname}/me), only worth making on the one
  // screen that actually shows a pressed/unpressed Like/Follow button.
  //
  // myStatsGenRef guards against a stale response here clobbering a since-
  // pressed Follow/Like/Mute button: this GET fires on mount and can still
  // be in flight when the user clicks Follow - if it resolves AFTER that
  // click's own applyChannelStats (its {liked,following,muted} snapshot
  // was read from the server before the click happened), it would
  // overwrite the just-set following:true straight back to false a moment
  // later. toggleFollow/toggleLike/toggleMute below all bump this ref
  // right after their own optimistic applyChannelStats, so a mount-time
  // response that lands later is recognised as stale and dropped instead
  // of applied.
  useEffect(() => {
    if (!c || !isLoggedIn) return;
    const gen = ++myStatsGenRef.current;
    fetchMyChannelStats(c.nickname).then((stats) => {
      if (myStatsGenRef.current === gen) applyChannelStats(c.id, stats);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, c?.nickname, isLoggedIn]);

  // Activity stats - GET /stats/channel/{nickname}/activity. Owner/member/
  // public gated server-side; a 403 (a private channel this account can't
  // see) just leaves the card hidden.
  useEffect(() => {
    if (!c || !isLoggedIn) return;
    let cancelled = false;
    fetchChannelActivity(c.nickname)
      .then((a) => {
        if (!cancelled) setActivity(a);
      })
      .catch(() => {
        if (!cancelled) setActivity(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, c?.nickname, isLoggedIn]);

  // The caller's member doc - only for a non-owner (an owner never needs
  // Leave/Follow). Shares the ['channelMemberMe', nickname, userId] query
  // key useChannelMembership.ts uses, so it's one GET across screens.
  useEffect(() => {
    if (!c || !isLoggedIn || isChannelOwner(c)) {
      setMyMember(null);
      return;
    }
    // profile.userId is '' until the first profile fetch resolves; a query
    // with it now hits GET /channels/{n}/member/ (empty {userId}) -> 404.
    // Leave myMember undefined; the effect re-runs when userId lands.
    if (!profile.userId) return;
    let cancelled = false;
    setMyMember(undefined);
    queryClient
      .query({
        queryKey: ['channelMemberMe', c.nickname, profile.userId],
        queryFn: () => fetchChannelMember(c.nickname, profile.userId),
      })
      .then((m) => {
        if (!cancelled) setMyMember(m ?? null);
      })
      .catch(() => {
        if (!cancelled) setMyMember(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, c?.nickname, isLoggedIn, profile.userId]);

  const goToSearch = (query: string) => {
    setSearchQuery(query);
    setSelectedTab('showcase');
    navigate('/showcase');
  };

  if (!c) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHeader title="Channel details" />
        <EmptyState icon={MessageCircle} title="Channel missing" subtitle="The channel is no longer available." />
      </div>
    );
  }

  const toggleLike = async () => {
    if (!isLoggedIn) {
      setError('Log in to like a channel.');
      return;
    }
    setBusy('like');
    try {
      // Routes through queryClient.query, same coordinator as the
      // read paths (see lib/queryClient.ts) - the `busy` disabled-button
      // state above already prevents a double-click from this same
      // control, but this also catches a stray duplicate trigger (e.g. a
      // fast Enter-key activation racing a click) from firing two
      // like/unlike POSTs for the same channel. retry: 0 - this is a
      // write, not a read; the client's default retry: 2 is meant for
      // idempotent GETs, and retrying a POST that failed for a real
      // reason (e.g. a 403) just wastes two more round trips without
      // ever succeeding.
      const result = c.liked
        ? await queryClient.query({ queryKey: queryKeys.channelLike(c.nickname, 'unlike'), queryFn: () => unlikeChannel(c.nickname), staleTime: 0, retry: 0 })
        : await queryClient.query({ queryKey: queryKeys.channelLike(c.nickname, 'like'), queryFn: () => likeChannel(c.nickname), staleTime: 0, retry: 0 });
      applyChannelStats(c.id, result);
      myStatsGenRef.current++;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update like.');
    } finally {
      setBusy(null);
    }
  };

  // Copies this channel (its description, tags, view settings and
  // template references) into a fresh channel owned by the current user,
  // under a "<nickname>-copy" handle, then opens its edit screen so they
  // can rename it. See dataApi's cloneChannel. Shown only to non-owners.
  const clone = async () => {
    if (!isLoggedIn) {
      setError('Log in to clone a channel.');
      return;
    }
    setBusy('clone');
    try {
      const created = await cloneChannel(c, 'channel_details');
      navigate(`/channels/${created.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to clone this channel.');
    } finally {
      setBusy(null);
    }
  };

  const toggleFollow = async () => {
    if (!isLoggedIn) {
      setError('Log in to follow a channel.');
      return;
    }
    setBusy('follow');
    try {
      if (c.following) {
        // See toggleLike's identical comment on routing through
        // queryClient.query, and queryKeys.channelFollow's own
        // comment on why the action is part of the key.
        const result = await queryClient.query({
          queryKey: queryKeys.channelFollow(c.nickname, 'unfollow'),
          queryFn: () => unfollowChannel(c.nickname),
          staleTime: 0,
          retry: 0,
        });
        applyChannelStats(c.id, result);
        myStatsGenRef.current++;
        // For a non-owner, following was the only reason this public
        // channel was in their "Channels" list (stats/channel_unfollow.wsl
        // drops it from user:channels:<userId> for a non-owner/non-
        // approved-member), so take it out right away - the mirror of the
        // addChannelToPersonalList call on the follow branch below. The
        // owner keeps every channel they own regardless of follow state,
        // so for them just reconcile the count against the server.
        if (isChannelOwner(c)) {
          syncChannels();
        } else {
          removeChannelFromPersonalList({ ...c, ...result });
        }
      } else {
        const result = await queryClient.query({
          queryKey: queryKeys.channelFollow(c.nickname, 'follow'),
          queryFn: () => followChannel(c.nickname),
          staleTime: 0,
          retry: 0,
        });
        applyChannelStats(c.id, result);
        myStatsGenRef.current++;
        // See addChannelToPersonalList's own comment - a channel reached
        // via Search isn't in the "Channels" list until you follow/join
        // it; mirror that locally instead of waiting for the next reload.
        addChannelToPersonalList({ ...c, ...result });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update follow.');
    } finally {
      setBusy(null);
    }
  };

  // Mutes/unmutes push notifications for this channel, for this account
  // only - see channel/mute.wsl. Unlike like/follow, this has no public
  // count and no membership side effect, so it's just an optimistic
  // applyChannelStats({muted}) rather than spreading a server response
  // (muteChannel/unmuteChannel return void by design - see dataApi.ts).
  const toggleMute = async () => {
    if (!isLoggedIn) {
      setError('Log in to mute a channel.');
      return;
    }
    setBusy('mute');
    const next = !c.muted;
    try {
      await queryClient.query({
        queryKey: queryKeys.channelMute(c.nickname, next ? 'mute' : 'unmute'),
        queryFn: () => (next ? muteChannel(c.nickname) : unmuteChannel(c.nickname)),
        staleTime: 0,
        retry: 0,
      });
      applyChannelStats(c.id, { muted: next });
      myStatsGenRef.current++;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update mute.');
    } finally {
      setBusy(null);
    }
  };

  // A "real" member - an owner invited them and they accepted, or their
  // join request was approved (member.respondedBy is a userId, not the
  // "self:follow" tag stats/channel_follow.wsl writes). These get "Leave
  // channel" instead of "Unfollow" - unfollow deliberately leaves a real
  // member doc in place (stats/channel_unfollow.wsl), so it's not an exit.
  const isRealMember =
    !!c && !isChannelOwner(c) && myMember?.status === 'accepted' && myMember.respondedBy !== 'self:follow';

  const handleLeave = async () => {
    if (!c) return;
    setBusy('leave');
    try {
      await leaveChannel(c.nickname);
      setShowLeaveConfirm(false);
      navigate('/channels');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to leave this channel.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader
        title={c.title}
        action={
          <div className="flex items-center gap-1">
            <IconButton icon={MessageCircle} onClick={() => navigate(`/channels/${c.id}`)} aria-label="Open chat" />
            {isChannelOwner(c) && (
              <IconButton icon={SquarePen} onClick={() => navigate(`/channels/${c.id}/edit`)} aria-label="Edit channel" />
            )}
          </div>
        }
      />

      <div className="flex flex-col items-center text-center gap-2">
        <AvatarView symbol={c.avatarSymbol} colorHex={c.accentHex} imageKey={c.avatarImageKey} size={110} zoomable onClick={() => setShowViewer(true)} />
        {/* Only a real edit entry point for the owner - this used to be
            an unconditional button navigating straight to /edit
            regardless of ownership, a second, completely ungated way in
            alongside the toolbar pencil button above (which was ALSO
            ungated, via the legacy always-true c.isOwner). */}
        {isChannelOwner(c) ? (
          <button
            onClick={() => navigate(`/channels/${c.id}/edit`)}
            className="text-xl font-bold underline decoration-black/20 dark:decoration-white/20"
          >
            {c.title}
          </button>
        ) : (
          <span className="text-xl font-bold">{c.title}</span>
        )}
        <button onClick={() => goToSearch(c.nickname)} className="font-medium underline text-black/70 dark:text-white/70">
          {c.nickname}
        </button>
        <span className="text-sm text-black/50 dark:text-white/50">Owner: {isChannelOwner(c) ? 'yes' : 'no'}</span>
      </div>

      <AppCard className="flex">
        <Stat title="Followers" value={c.followers} />
        <Stat title="Likes" value={c.likes} />
      </AppCard>

      {activity && (
        <AppCard className="flex flex-col gap-3">
          <div className="flex">
            <Stat title="Signals" value={activity.messageCount} />
            <Stat title="Members" value={activity.memberCount} />
            <Stat title="Watching now" value={activity.viewersNow} />
          </div>
          {(activity.watchingGuests > 0 || activity.pendingCount > 0) && (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-black/50 dark:text-white/50">
              {activity.viewersNow > 0 && (
                <span>
                  {activity.watchingMembers} member{activity.watchingMembers === 1 ? '' : 's'} · {activity.watchingGuests} guest
                  {activity.watchingGuests === 1 ? '' : 's'} watching
                </span>
              )}
              {activity.pendingCount > 0 && <span>{activity.pendingCount} pending request{activity.pendingCount === 1 ? '' : 's'}</span>}
            </div>
          )}
        </AppCard>
      )}

      <div className="flex gap-3">
        <Button variant="primary" full onClick={toggleLike} disabled={busy !== null}>
          {c.liked ? 'Unlike' : 'Like'}
        </Button>
        {isRealMember ? (
          <Button variant="destructive" full onClick={() => setShowLeaveConfirm(true)} disabled={busy !== null}>
            Leave channel
          </Button>
        ) : (
          <Button variant="bordered" full onClick={toggleFollow} disabled={busy !== null}>
            {c.following ? 'Unfollow' : 'Follow'}
          </Button>
        )}
        {isLoggedIn && (
          <IconButton
            icon={c.muted ? BellOff : Bell}
            className={`shrink-0 border ${c.muted ? 'border-amber-500/40 text-amber-600 dark:text-amber-400' : 'border-black/15 dark:border-white/20'}`}
            onClick={toggleMute}
            disabled={busy !== null}
            aria-label={c.muted ? 'Unmute notifications for this channel' : 'Mute notifications for this channel'}
            title={c.muted ? 'Notifications muted' : 'Mute notifications'}
          />
        )}
      </div>

      {!isChannelOwner(c) && cloneOn && (
        <Button variant="bordered" full onClick={clone} loading={busy === 'clone'} disabled={busy !== null}>
          <Copy size={16} />
          Clone this channel
        </Button>
      )}

      <AppCard className="flex flex-col gap-3">
        <span className="font-semibold">Description</span>
        <FlowHashtagText text={c.description} onTap={goToSearch} />
        {c.tags.length > 0 && <WrapTags tags={c.tags} onTap={goToSearch} />}
      </AppCard>

      <Sheet open={showViewer} onClose={() => setShowViewer(false)} title={c.title}>
        <div className="flex flex-col items-center gap-4 py-6">
          <AvatarView symbol={c.avatarSymbol} colorHex={c.accentHex} imageKey={c.avatarImageKey} size={220} />
          <span className="text-2xl font-bold">{c.title}</span>
        </div>
      </Sheet>

      <Sheet
        open={showLeaveConfirm}
        onClose={() => {
          if (busy !== 'leave') setShowLeaveConfirm(false);
        }}
        title="Leave this channel?"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-black/70 dark:text-white/70">
            You'll lose access to its messages and need a new invite to rejoin.
          </p>
          <div className="flex gap-2">
            <Button variant="bordered" full disabled={busy === 'leave'} onClick={() => setShowLeaveConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" full loading={busy === 'leave'} disabled={busy === 'leave'} onClick={handleLeave}>
              Leave channel
            </Button>
          </div>
        </div>
      </Sheet>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
    </div>
  );
}
